import React, { useEffect, useState } from "react";
import "./style.scss";
import CrownWhite from "../../../../icons/CrownWhite";
import TeamIcon from "../../../../icons/TeamIcon";
import { ProgressBar } from "react-bootstrap";
import HomeGreyIcon from "../../../../icons/HomeGreyIcon";
import CardBlueIcon from "../../../../icons/CardBlueIcon";
import { FaAngleRight } from "react-icons/fa";
import CouponTagIcon from "../../../../icons/CouponTagIcon";
import DownloadGreenIcon from "../../../../icons/DownloadGreenIcon";
import SettingPurpleIcon from "../../../../icons/SettingPurpleIcon";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../../../feature-module/router/all_routes";
import { useDispatch, useSelector } from "react-redux";
import { getSubscriptionOverview } from "../../../data/redux/pricing/_pricingAction";
import { formatDateStringType } from "../../../../constants/date";
import { toast } from "react-toastify";
import { getUserProfile } from "../../../data/redux/user/_userAction";

interface OverviewTabProps {
  activeTab?: string;
  onNavigateToTab?: (tab: string) => void;
}
const PLAN_NAMES: Record<string, string> = {
  "com.monthlyBasic.tracNest": "Basic Monthly",
  "com.yearlyBasic.tracNest": "Basic Yearly",
  "com.premiumMonthly.tracNest": "Premium Monthly",
};

const toAmount = (value: any): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
};

const formatAmount = (value: any): string | null => {
  const amount = toAmount(value);
  return amount === null ? null : `$${amount.toFixed(2)}`;
};

const OverviewTab: React.FC<OverviewTabProps> = ({
  activeTab,
  onNavigateToTab,
}) => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const routes = all_routes;

  const [billingOverview, setBillingOverview] = useState<any>({});
  const planData = useSelector((state: any) => state?.user?.userDetail?.user_subscription);
  const planName = PLAN_NAMES[planData?.apple_plan_id] || "Unknown Plan";
  const amoutnPaid = planData?.amount_paid;
  const subscription_type = billingOverview?.subscription_type || planData?.type;
  const billingCycleLabel =
    subscription_type?.toLowerCase() == "monthly" ? "Month" : "Year";

  // Coupon / discount details (only rendered when the API reports an applied coupon)
  const rawPlanPrice = billingOverview?.plan_price?.price ?? amoutnPaid;
  const listPrice =
    formatAmount(billingOverview?.original_amount) || formatAmount(rawPlanPrice);
  const paidPrice = formatAmount(billingOverview?.amount_paid);
  const discountValue = toAmount(billingOverview?.discount_amount);
  const couponCode = billingOverview?.coupon_code_applied;
  const isCouponApplied = Boolean(billingOverview?.is_coupon_applied);
  const hasDiscountValue = isCouponApplied && discountValue !== null && discountValue > 0;
  const discountPrice = formatAmount(discountValue);
  const headlinePrice =
    isCouponApplied && paidPrice
      ? paidPrice
      : formatAmount(rawPlanPrice) ?? `$${rawPlanPrice ?? ""}`;
  const nextBillingDate = formatDateStringType(
    billingOverview?.next_payment_attempt || billingOverview?.trial_end
  );


  useEffect(() => {
    if (activeTab === "Overview") {
      getBillingOverview();
      dispatch(getUserProfile({})); 
    }
  }, [activeTab]);

  const getBillingOverview = async () => {
    try {
      const response = await dispatch(getSubscriptionOverview());
      setBillingOverview(response?.payload?.data);
      return;
    } catch (error) {
      console.error("Failed to fetch billing overview:", error);
    }
  };

  const handleChangePlan = () => {
    if (billingOverview?.platform?.toLowerCase() === "ios") {
      toast.error(
        "This subscription was purchased via in-app purchase. Plan changes are not available here. Please manage or cancel your subscription through the App Store."
      );
      return;
    }

    navigate(routes.planList);
  };

  return (
    <div className="overviewTab billingTabWrap">
      {!billingOverview?.trial_expired && (
        <div className="trailAlert text-center">
          <span>Free Trial Active:</span>{" "}
          {billingOverview?.trial_remaining_days} days remaining. Upgrade now to
          continue using all features.
        </div>
      )}

      <div className="professionalPlanWrapper">
        <div className="bgBlue">
          <div className="planSummary">
            <h3>{billingOverview?.plan_price?.name || planName} Plan</h3>
            <div className="priceRow">
              <p>
                {headlinePrice}/{billingCycleLabel}
              </p>
              {hasDiscountValue && listPrice && listPrice !== headlinePrice && (
                <span className="strikePrice">{listPrice}</span>
              )}
            </div>
            {isCouponApplied && (
              <span className="couponBadge">
                <CouponTagIcon />
                {couponCode ? `${couponCode} applied` : "Coupon applied"}
                {hasDiscountValue ? ` · You save ${discountPrice}` : ""}
              </span>
            )}
          </div>
          <CrownWhite />
        </div>

        <div className="nextBilling">
          {isCouponApplied && (
            <div className="couponSummary">
              <h4>
                <CouponTagIcon /> Coupon Applied
              </h4>

              {listPrice && (
                <div className="summaryRow">
                  <span>Plan price</span>
                  <label>{listPrice}</label>
                </div>
              )}

              <div className="summaryRow">
                <span>
                  Coupon discount
                  {couponCode && <em className="couponCode">{couponCode}</em>}
                </span>
                <label className="discountValue">
                  {hasDiscountValue ? `-${discountPrice}` : discountPrice ?? "$0.00"}
                </label>
              </div>

              {paidPrice && (
                <div className="summaryRow summaryTotal">
                  <span>Amount paid</span>
                  <label>{paidPrice}</label>
                </div>
              )}

              {hasDiscountValue && listPrice && (
                <p className="renewalNote">
                  Your offer price is valid for the first{" "}
                  {billingCycleLabel.toLowerCase()} only. Starting next{" "}
                  {billingCycleLabel.toLowerCase()}
                  {nextBillingDate ? ` (${nextBillingDate})` : ""}, you’ll be
                  charged {listPrice}/{billingCycleLabel.toLowerCase()}.
                </p>
              )}
            </div>
          )}

          <div className="d-flex justify-content-between gap-3 align-items-center mb-3">
            <div className="d-flex flex-column">
              <span>Next billing date</span>
              <label>{nextBillingDate}</label>
            </div>
            <button
              className="primaryButton"
              onClick={handleChangePlan}
            >
              Change Plan
            </button>
          </div>

          <div className="row align-items-stretch">
            <div className="col-sm-6">
              <div className="teamUsage">
                <div className="d-flex justify-content-between align-items-center gap-2 mb-1">
                  <label>CPA Invites</label>
                  {billingOverview?.tax_professional}
                  {/* <TeamIcon /> */}
                </div>
                {/* <div className='progessWrap d-flex flex-column gap-2'>
                  <div className='d-flex gap-2 justify-content-between'>
                    <span>Users</span>
                    <span>3 of 5</span>
                  </div>
                </div> */}
              </div>
            </div>

            <div className="col-sm-6">
              <div className="propertyUsage">
                <div className="d-flex justify-content-between gap-2 mb-1">
                  <label>Spouse Invites</label>
                  {billingOverview?.spouse}
                  {/* <HomeGreyIcon /> */}
                </div>
                {/* <div className='progessWrap d-flex flex-column gap-2'>
                  <div className='d-flex gap-2 justify-content-between'>
                    <span>Properties</span>
                    <span>12/Unlimited</span>
                  </div>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="paymentBlocks">
        <div className="row align-items-stretch">
          {/* <div className='col-md-4 mb-3 d-flex'>
            <div
              className='paymentMethod p-4 w-100'
              onClick={() => onNavigateToTab && onNavigateToTab("Payment")}
            >
              <CardBlueIcon />
              <h3>Payment Methods</h3>
              <p>Manage your payment information</p>
              <FaAngleRight />
            </div>
          </div> */}

          <div className="col-md-4 mb-3 d-flex ">
            <div
              className="billingHistoryMethod p-4 w-100"
              onClick={() => onNavigateToTab && onNavigateToTab("History")}
            >
              <DownloadGreenIcon />
              <h3>Billing History</h3>
              <p>View and download invoices</p>
              <FaAngleRight />
            </div>
          </div>
          <div className="col-md-4 mb-3 d-flex">
            <div
              className="managePlanMethod p-4 w-100"
              onClick={() => onNavigateToTab && onNavigateToTab("Settings")}
            >
              <SettingPurpleIcon />
              <h3>Manage Plan</h3>
              <p>Upgrade, downgrade, or cancel</p>
              <FaAngleRight />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
